<template>
  <Disclosure as="nav" class="bg-white border-b border-gray-300 shadow-md relative z-[60]">
    <LoadingOverlay :show="isLoading" />

    <div class="mx-auto w-full px-2 sm:px-4 lg:px-8">
      <div class="flex justify-between h-16">
        <div class="flex px-2 lg:px-0">
          <div class="flex-shrink-0 flex items-center">
            <img class="logo block lg:hidden" :src="require('/public/assets/logo.png')" alt="Your Company" />
            <img class="logo hidden lg:block" :src="require('/public/assets/logo.png')" alt="Your Company" />
          </div>

          <div class="hidden lg:ml-6 lg:flex lg:space-x-8">
            <Menu
              as="div"
              class="relative mt-5"
              v-for="(item, index) in filteredMenuItems"
              :key="index"
              @click="navigateTo(item.text)"
            >
              <MenuButton
                class="inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium leading-5 focus:outline-none transition duration-150 ease-in-out"
                :class="item.current ? 'border-yellow-500 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
              >
                <component :is="item.icon" class="mr-2 h-5 w-5" />
                {{ item.text }}
              </MenuButton>
            </Menu>
          </div>
        </div>

        <div class="flex lg:hidden">
          <DisclosureButton
            class="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-yellow-500"
          >
            <span class="sr-only">Open main menu</span>
            <Bars3Icon class="h-6 w-6" aria-hidden="true" />
          </DisclosureButton>
        </div>

        <div class="hidden lg:flex lg:items-center gap-3">
          <!-- Status + Phone -->
            <Menu as="div" class="relative z-[80]">
              <MenuButton
                class="flex items-center gap-2 px-3 h-10 rounded-lg hover:bg-gray-100 focus:outline-none ring-1 ring-transparent hover:ring-gray-200 transition"
              >
                <span class="h-2.5 w-2.5 rounded-full" :class="presenceDotTopClass"></span>
                <span class="text-sm font-semibold text-gray-800">{{ presenceTopLabel }}</span>
                <ChevronDownIcon class="h-4 w-4 text-gray-500" />
              </MenuButton>

              <transition
                enter-active-class="transition ease-out duration-120"
                enter-from-class="transform opacity-0 scale-95 translate-y-1"
                enter-to-class="transform opacity-100 scale-100 translate-y-0"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100 translate-y-0"
                leave-to-class="transform opacity-0 scale-95 translate-y-1"
              >
                <MenuItems
                  class="origin-top-right absolute right-0 mt-2 w-72 rounded-2xl shadow-xl bg-white ring-1 ring-black/5 focus:outline-none overflow-hidden z-[9999]"
                >
                  <div class="px-4 py-3 border-b border-gray-100">
                    <div class="flex items-center justify-between">
                      <div class="min-w-0">
                        <div class="text-sm font-semibold text-gray-900 truncate">{{ displayName }}</div>
                        <div class="text-xs text-gray-500 mt-0.5">Ext {{ extension }}</div>
                      </div>
                      <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border" :class="regBadgeClass">{{ regLabel }}</span>
                    </div>
                  </div>
                  <div class="py-2">
                    <MenuItem v-for="opt in presenceOptions" :key="opt" v-slot="{ active }">
                      <button class="w-full px-4 py-2.5 text-sm flex items-center justify-between" :class="active ? 'bg-gray-50 text-gray-900' : 'text-gray-700'" @click="setPresence(opt)">
                        <div class="flex items-center gap-3">
                          <span class="h-2.5 w-2.5 rounded-full" :class="presenceDotClass(opt)"></span>
                          <div class="font-semibold">{{ opt }}</div>
                        </div>
                        <CheckIcon v-if="presence === opt" class="h-5 w-5 text-gray-700" />
                      </button>
                    </MenuItem>
                  </div>
                </MenuItems>
              </transition>
            </Menu>

            <button
              class="h-10 w-10 rounded-full bg-gray-100 hover:bg-gray-200 active:bg-gray-200 flex items-center justify-center focus:outline-none ring-1 ring-gray-200 transition"
              type="button" @click="togglePhone" :title="phoneVisible ? 'Hide phone' : 'Show phone'"
            >
              <PhoneIcon class="h-5 w-5 text-gray-700" />
            </button>

          <!-- Avatar dropdown (always visible) -->
          <Menu as="div" class="relative z-[80]">
            <MenuButton class="h-9 w-9 rounded-full bg-gray-100 hover:ring-2 hover:ring-gray-300 flex items-center justify-center focus:outline-none transition cursor-pointer overflow-hidden">
              <img v-if="navAvatarUrl" class="h-full w-full object-cover" :src="navAvatarUrl" alt="Avatar" />
              <span v-else class="text-xs font-bold text-gray-600">{{ navInitials }}</span>
            </MenuButton>
            <transition
              enter-active-class="transition ease-out duration-120"
              enter-from-class="transform opacity-0 scale-95 translate-y-1"
              enter-to-class="transform opacity-100 scale-100 translate-y-0"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100 translate-y-0"
              leave-to-class="transform opacity-0 scale-95 translate-y-1"
            >
              <MenuItems class="origin-top-right absolute right-0 mt-2 w-48 rounded-xl shadow-lg bg-white ring-1 ring-black/5 focus:outline-none overflow-hidden z-[9999]">
                <div class="py-1">
                  <MenuItem v-slot="{ active }">
                    <button class="w-full text-left px-4 py-2 text-sm" :class="active ? 'bg-gray-50' : ''" @click="navigateToProfile">
                      Your Profile
                    </button>
                  </MenuItem>
                  <MenuItem v-slot="{ active }">
                    <button class="w-full text-left px-4 py-2 text-sm text-red-600" :class="active ? 'bg-red-50' : ''" @click="signOut">
                      Sign Out
                    </button>
                  </MenuItem>
                </div>
              </MenuItems>
            </transition>
          </Menu>
        </div>
      </div>
    </div>

    <DisclosurePanel class="lg:hidden">
      <div class="px-2 pt-2 pb-3 space-y-1">
        <a
          href="#"
          class="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
        >
          Dashboard
        </a>
      </div>
    </DisclosurePanel>
  </Disclosure>

  <!-- Greeting Alert Toast -->
  <Transition name="toast">
    <div v-if="showGreetingAlert" class="fixed bottom-4 right-4 z-[100] w-80 rounded-xl border border-amber-200 bg-white shadow-lg overflow-hidden">
      <div class="p-4">
        <div class="flex items-start gap-3">
          <div class="shrink-0 w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
            <ExclamationTriangleIcon class="w-5 h-5 text-amber-600" />
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-gray-800">Voicemail greeting not set</p>
            <p class="text-xs text-gray-500 mt-0.5">Callers will hear a generic greeting. Record a personal one so they know they've reached you.</p>
            <div class="mt-2 flex gap-2">
              <button class="px-2.5 py-1 text-xs rounded-md bg-tvBlue text-white hover:opacity-90 font-medium" @click="openGreetingFromAlert">Set Up Now</button>
              <button class="px-2.5 py-1 text-xs rounded-md border hover:bg-gray-50 text-gray-600" @click="dismissGreetingAlert">Later</button>
            </div>
          </div>
          <button class="shrink-0 p-0.5 rounded hover:bg-gray-100" @click="dismissGreetingAlert">
            <XMarkIcon class="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>
    </div>
  </Transition>

  <GreetingRecorderModal
    v-if="showGreetingModalFromNav"
    :has-existing="false"
    @close="showGreetingModalFromNav = false"
    @saved="onGreetingSavedFromNav"
  />
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import LoadingOverlay from '@/views/LoadingOverlay.vue'
import axios from 'axios'
import VoicemailService from '@/services/VoicemailService'
import GreetingRecorderModal from '@/views/communications/components/GreetingRecorderModal.vue'
import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import {
  Bars3Icon,
  PhoneIcon,
  ChatIcon,
  GlobeAltIcon,
  ChartBarIcon,
  ChevronDownIcon,
  CheckIcon
} from '@heroicons/vue/20/solid'

const router = useRouter()
const store = useStore()

const isLoading = computed(() => store.state.isLoading)

const menuItems = ref([
  { text: 'COMMUNICATIONS', current: false, icon: PhoneIcon },
  { text: 'SALES', current: false, icon: ChatIcon },
  { text: 'PBX', current: false, icon: GlobeAltIcon },
  { text: 'MONITORING', current: false, icon: ChartBarIcon }
])

const filteredMenuItems = computed(() => {
  return menuItems.value.filter(item => {
    if (item.text === 'COMMUNICATIONS' && store.getters['customerRoles/hasCustomerRole']('Communications')) return true
    if (item.text === 'SALES' && store.getters['customerRoles/hasCustomerRole']('Sales Agent')) return true
    if (item.text === 'PBX' && store.getters['customerRoles/hasCustomerRole']('PBX Admin')) return true
    if (item.text === 'MONITORING' && store.getters['customerRoles/hasCustomerRole']('PBX Admin')) return true
    return false
  })
})

const navigateTo = (menuItem) => {
  if (menuItem === 'COMMUNICATIONS') router.push({ name: 'communications' })
  else if (menuItem === 'SALES') router.push({ name: 'sales' })
  else if (menuItem === 'PBX') router.push({ name: 'pbx' })
  else if (menuItem === 'MONITORING') router.push({ name: 'monitoring' })
}

const navigateToProfile = () => router.push({ name: 'profile' })

const signOut = () => {
  sessionStorage.removeItem('admin_customer_id')
  sessionStorage.removeItem('admin_mode')
  store.dispatch('auth/logout')
  window.location.href = '/login'
}

const phoneVisible = computed(() => store.state.phone?.ui?.visible === true)
const togglePhone = () => store.dispatch('phone/togglePhone')

// ------------------------------------------------------------
// NEW: pull name/ext from phone module (my-softphone)
// ------------------------------------------------------------
const phoneProfileName = computed(() => store.getters['phone/profileName'])
const phoneProfileExt = computed(() => store.getters['phone/profileExtension'])

// Name + extension should come from phone module (my-softphone)
// Keep existing fallbacks just in case phone hasn't bootstrapped yet.
const displayName = computed(() => (
  store.state.phone?.ui?.displayName ||
  store.state.auth?.user?.name ||
  store.state.auth?.user?.displayName ||
  store.state.auth?.account?.fullName ||
  process.env.VUE_APP_SIP_DISPLAY_NAME ||
  'User'
))

const extension = computed(() => (
  store.state.phone?.ui?.extension ||
  store.state.auth?.user?.extension ||
  store.state.auth?.user?.ext ||
  process.env.VUE_APP_SIP_USERNAME ||
  '—'
))


// SIP registration badge stays from phone module
const regState = computed(() => store.state.phone?.ua?.regState || 'Unregistered')
const regLabel = computed(() => (regState.value === 'Registered' ? 'Registered' : 'Not Registered'))

const regBadgeClass = computed(() => {
  if (regState.value === 'Registered') return 'bg-green-50 text-green-700 border-green-200'
  return 'bg-gray-100 text-gray-700 border-gray-200'
})

// Presence comes from presence module
const presenceTopLabel = computed(() => store.getters['presence/myLabel'] || 'Offline')
const presenceTone = computed(() => store.getters['presence/myTone'] || 'off')

// UI checkmark uses whatever your dropdown currently set (phone.ui.presence)
const presence = computed(() => store.state.phone?.ui?.presence || 'Available')

const presenceDotTopClass = computed(() => {
  if (presenceTone.value === 'good') return 'bg-green-500'
  if (presenceTone.value === 'warn') return 'bg-amber-500'
  if (presenceTone.value === 'bad') return 'bg-red-500'
  return 'bg-gray-400'
})

const presenceOptions = ['Available', 'Away', 'Busy', 'Do Not Disturb', 'Offline']

const setPresence = (opt) => {
  // keep UI checkmark state
  store.dispatch('phone/setPresence', opt)

  // map to backend status strings
  let status = opt.toLowerCase().replace(/\s+/g, '_')
  if (opt === 'Do Not Disturb') status = 'dnd'
  if (opt === 'Away') status = 'away'

  store.dispatch('presence/setMyPresence', { status, message: '' })
}

const presenceDotClass = (opt) => {
  switch (opt) {
    case 'Available': return 'bg-green-500'
    case 'Away': return 'bg-amber-500'
    case 'Busy': return 'bg-red-500'
    case 'Do Not Disturb': return 'bg-purple-500'
    case 'Offline': return 'bg-gray-400'
    default: return 'bg-gray-400'
  }
}

// Ensure navbar always has profile info even before opening the phone
/* ===== Nav Avatar ===== */
const navAvatarUrl = ref(null)
const navInitials = ref('?')

async function loadNavAvatar() {
  try {
    const res = await axios.get('user')
    const u = res?.data
    const f = (u?.first_name || ' ')[0] || ''
    const l = (u?.last_name || ' ')[0] || ''
    navInitials.value = (f + l).toUpperCase().trim() || '?'
    if (u?.avatar_url && u.avatar_url.startsWith('avatars/')) {
      const base = (axios.defaults.baseURL || '').replace(/\/api\/?$/, '')
      navAvatarUrl.value = base + `/api/user/${u.id}/avatar`
    }
  } catch (e) {}
}


/* ===== Greeting alert ===== */
const showGreetingAlert = ref(false)
const showGreetingModalFromNav = ref(false)

async function checkGreetingStatus() {
  // Only check if user dismissed flag isn't set for this session
  if (sessionStorage.getItem('greeting_alert_dismissed')) return

  try {
    const res = await VoicemailService.userGreeting()
    if (!res?.data?.has_greeting) {
      // Small delay so the page loads first
      setTimeout(() => { showGreetingAlert.value = true }, 2000)
    }
  } catch (e) {
    // Silently fail — user may not have a SIP user yet
  }
}

function dismissGreetingAlert() {
  showGreetingAlert.value = false
  sessionStorage.setItem('greeting_alert_dismissed', '1')
}

function openGreetingFromAlert() {
  showGreetingAlert.value = false
  sessionStorage.setItem('greeting_alert_dismissed', '1')
  showGreetingModalFromNav.value = true
}

function onGreetingSavedFromNav() {
  showGreetingModalFromNav.value = false
}

onMounted(() => {
  loadNavAvatar()

  store.dispatch('phone/bootstrap').catch(() => {})
  checkGreetingStatus()
})
</script>

<style scoped>
.toast-enter-active { transition: all 0.3s ease-out; }
.toast-leave-active { transition: all 0.2s ease-in; }
.toast-enter-from { opacity: 0; transform: translateY(20px); }
.toast-leave-to { opacity: 0; transform: translateX(20px); }

.logo {
  width: 160px;
}

.avatar-container {
  background-color: white;
  border-radius: 50%;
  padding: 2px;
}
</style>
